import axios from 'axios';
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { env } from '@/config/env';
import type { ApiResponse } from '@/shared/types';

// 使用全局 message 实例（将在 App 组件中初始化）
let messageApi: any = null;

export const setMessageApi = (api: any) => {
  messageApi = api;
};

export const getMessageApi = () => messageApi;

class HttpClient {
  private instance: AxiosInstance;

  constructor() {
    this.instance = axios.create({
      baseURL: env.apiBaseUrl,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('admin_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        const { data } = response;
        
        // 如果后端返回的是标准格式
        if (data.code !== undefined) {
          if (data.code === 200 || data.code === 0) {
            return data.data;
          } else {
            messageApi?.error(data.message || '请求失败');
            return Promise.reject(new Error(data.message));
          }
        }
        
        // 直接返回数据
        return data;
      },
      (error) => {
        if (error.response) {
          const { status, data, config } = error.response;
          
          // 检查是否是登录接口，如果是则不在这里显示错误消息
          const isLoginRequest = config?.url?.includes('/auth/login');
          
          if (!isLoginRequest) {
            // 非登录接口才显示通用错误消息
            switch (status) {
              case 401:
                messageApi?.error('未授权，请重新登录');
                localStorage.removeItem('admin_token');
                window.location.href = '/admin/login';
                break;
              case 403:
                messageApi?.error('没有权限访问');
                break;
              case 404:
                messageApi?.error('请求的资源不存在');
                break;
              case 500:
                messageApi?.error('服务器错误');
                break;
              default:
                messageApi?.error(data?.message || '请求失败');
            }
          }
        } else if (error.request) {
          messageApi?.error('网络错误，请检查网络连接');
        } else {
          messageApi?.error('请求配置错误');
        }
        
        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.get(url, config);
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.post(url, data, config);
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.put(url, data, config);
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.patch(url, data, config);
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    return this.instance.delete(url, config);
  }

  async upload<T = any>(url: string, formData: FormData, config?: AxiosRequestConfig): Promise<T> {
    // 删除默认的 Content-Type，让浏览器自动设置 multipart/form-data 及其 boundary
    return this.instance.post(url, formData, {
      ...config,
      headers: {
        ...config?.headers,
        'Content-Type': undefined,
      },
    });
  }
}

export const httpClient = new HttpClient();
